import Component from '@glimmer/component';
import { service } from '@ember/service';
import { trackedFunction } from 'reactiveweb/function';
import { LinkTo } from '@ember/routing';
import { PhTable, PhChartBar } from 'ember-phosphor-icons';
import { eq } from 'tio-ui/utilities';
import { t } from 'ember-intl';
import { fetchReportViewCounts } from '../utils/report-view-counts';
import type StoreService from 'ember-data/store';
import type Report from '../models/reports';
import { tracked } from '@glimmer/tracking';

interface PartnerReportsPageSignature {
  Args: {
    companyId: string;
    viewerRoute: string;
  };
}

export default class PartnerReportsPage extends Component<PartnerReportsPageSignature> {
  @service declare store: StoreService;

  @tracked viewCounts: Record<string, number> = {};

  reportsData = trackedFunction(this, async () => {
    const results = await this.store.query<Report>('report', {
      filter: { report_type: 'ta_report' },
      page: { limit: 200 },
    });
    const reports = Array.from(results as Iterable<Report>);

    const ids = reports.map((r) => r.id).filter((id): id is string => Boolean(id));
    fetchReportViewCounts(this.store, ids)
      .then((counts) => {
        this.viewCounts = counts;
      })
      .catch(() => {});

    return reports;
  });

  get isLoading() {
    return this.reportsData.isLoading;
  }

  get publishedReports() {
    const all = this.reportsData.value ?? [];
    const companyId = Number(this.args.companyId);
    return all.filter((r) => r.isPublished && !r.isArchived && r.isVisibleToCompany(companyId));
  }

  queryFor = (report: Report) => {
    return {
      reportId: report.id,
      companyId: this.args.companyId,
    };
  };

  viewCountFor = (report: Report) => {
    const id = report.id;
    return id ? (this.viewCounts[id] ?? 0) : 0;
  };

  static MAX_DESCRIPTION_LENGTH = 120;

  truncateDescription = (report: Report) => {
    const desc = report.description;
    if (!desc) return '';
    if (desc.length <= PartnerReportsPage.MAX_DESCRIPTION_LENGTH) return desc;
    return desc.slice(0, PartnerReportsPage.MAX_DESCRIPTION_LENGTH) + '…';
  };

  <template>
    {{#if this.isLoading}}
      <div class="py-8 text-center text-gray-500">{{t "reporting.loading"}}</div>
    {{else if this.publishedReports.length}}
      <div class="flex flex-col gap-2">
        {{#each this.publishedReports as |report|}}
          <div
            class="flex flex-col gap-1 rounded-lg border border-gray-200 bg-white px-4 py-3 transition hover:border-ocean-300 hover:shadow-sm dark:border-gray-700 dark:bg-gray-900 dark:hover:border-ocean-600"
          >
            {{! Primary Row: Clickable Name }}
            <div class="flex items-center gap-3">
              <LinkTo
                class="group flex min-w-0 items-center gap-2 text-sm font-medium text-ocean-800 hover:text-ocean-600 dark:text-ocean-300 dark:hover:text-ocean-400"
                @route={{@viewerRoute}}
                @query={{this.queryFor report}}
              >
                <PhTable
                  class="h-4 w-4 shrink-0 text-ocean-400 group-hover:text-ocean-600 dark:text-ocean-300"
                />
                <span class="truncate">{{report.name}}</span>
              </LinkTo>
            </div>

            {{! Description }}
            {{#let (this.truncateDescription report) as |desc|}}
              {{#if desc}}
                <p class="pl-6 text-xs text-tio-gray-600 dark:text-gray-400">{{desc}}</p>
              {{/if}}
            {{/let}}

            {{! Secondary Row: Date + Stats }}
            <div class="flex items-center gap-4 pl-6 text-xs text-tio-gray-400">
              <span>{{t "reporting.updated" date=report.formattedUpdatedAt}}</span>
              {{#let (this.viewCountFor report) as |count|}}
                {{#if count}}
                  <span class="text-tio-gray-200">·</span>
                  <span
                    class="inline-flex items-center gap-1 text-ocean-400 dark:text-ocean-300"
                    title="{{count}} {{if (eq count 1) (t 'reporting.view') (t 'reporting.views')}}"
                  >
                    <PhChartBar class="h-3 w-3" />
                    {{count}}
                    {{t "reporting.views"}}
                  </span>
                {{/if}}
              {{/let}}
            </div>
          </div>
        {{/each}}
      </div>
    {{else}}
      <div class="py-8 text-center">
        <p class="text-gray-500 dark:text-gray-400">{{t "reporting.no_reports"}}</p>
      </div>
    {{/if}}
  </template>
}
